import { Formik, FormikHelpers } from 'formik';
import React, { useRef } from 'react';
import { useApiRequest } from '../../../api/useApiRequest';
import { AssertUserHasPermission } from '../../../authorisation/AssertUserHasPermission';
import { assertNotNull } from '../../../helpers/nullHelpers';
import { RouteComponentProps } from '../../../helpers/RouteComponentProps';
import { urls } from '../../../helpers/urls';
import { BreadcrumbBar, BreadcrumbLink, BreadcrumbSeparator } from '../../../layout/Breadcrumbs';
import { PageContainer } from '../../../layout/Layout';
import { FormErrorBox } from '../../../shared/form/Form';
import { Header1 } from '../../../shared/Headers';
import { Project, ProjectHeader } from '../../projects/project-section/ProjectHeader';
import { projectUrls } from '../../projects/projectUrls';
import { GetDataForQuoteOrDesignForm } from '../../shared/quote-or-design-form/GetDataForQuoteOrDesignForm';
import { CreateDesignResponse } from '../createDesignResponse';
import { mapDesignFormModelToCreateCommand, UpdateDesignCommand } from '../createUpdateDesignDto';
import { DesignForm } from '../design-form/DesignForm';
import { NextStepAfterSavingDesign } from '../design-form/DesignFormButtons';
import { navigateAfterSavingDesign } from '../design-form/navigateAfterSavingDesign';
import { DesignFormModel, designFormModelValidator } from '../design-form/designFormModel';
import { DesignResponse, mapDesignResponseToFormModel } from '../designResponse';
import { GetRequest } from '../../../shared/GetRequest';

type Props = { project: Project } & RouteComponentProps<{ projectId: string; designId: string }>;

export const CreateDesignRevision = ({ project, designId }: Props) => {
  const getDesignRequest = useApiRequest<DesignResponse>({
    httpMethod: 'get',
    endpointUrl: `projects/${project.projectId}/designs/${designId}`,
  });

  const createDesignRequest = useApiRequest<CreateDesignResponse, UpdateDesignCommand>({
    httpMethod: 'post',
    endpointUrl: `projects/${project.projectId}/designs`,
  });

  const nextStepAfterSave = useRef<NextStepAfterSavingDesign>('BackToProject');

  return (
    <GetRequest getDataRequest={getDesignRequest}>
      {({ response: designResponse }) => {
        const onSubmit = (formModel: DesignFormModel, formikHelpers: FormikHelpers<DesignFormModel>) => {
          const createDesignCommand: UpdateDesignCommand = mapDesignFormModelToCreateCommand(
            formModel,
            project.projectId,
            assertNotNull(getDesignRequest.response).createdFromQuoteId,
            designResponse.pipes,
          );

          if (createDesignRequest.inProgress) {
            return;
          }

          return createDesignRequest.sendRequest({
            requestBody: createDesignCommand,
            onSuccess: response => {
              formikHelpers.setSubmitting(false);

              return navigateAfterSavingDesign(
                project.projectId,
                response.designId,
                createDesignCommand.design.isDraft,
                nextStepAfterSave.current,
              );
            },
            onFailure: () => {
              formikHelpers.setSubmitting(false);
            },
          });
        };

        return (
          <>
            <BreadcrumbBar>
              <BreadcrumbLink to={urls.home}>Projects</BreadcrumbLink>
              <BreadcrumbSeparator />
              <BreadcrumbLink to={projectUrls.projectDetails(project.projectId)}>
                {project.projectReference}
              </BreadcrumbLink>
              <BreadcrumbSeparator />
              <div>{designResponse.fullName}</div>
              <BreadcrumbSeparator />
              <div>Revise Design</div>
            </BreadcrumbBar>
            <ProjectHeader project={project} />
            <PageContainer>
              <AssertUserHasPermission requiredPermission="Edit">
                <GetDataForQuoteOrDesignForm project={project}>
                  {partsOptions => (
                    <>
                      <Header1>New Revision</Header1>
                      <Formik<DesignFormModel>
                        onSubmit={onSubmit}
                        initialValues={mapDesignResponseToFormModel(
                          designResponse,
                          partsOptions,
                          project.buildTypeCode,
                        )}
                        validate={formModel => designFormModelValidator.validate(formModel)}
                      >
                        {formProps => (
                          <DesignForm
                            {...formProps}
                            project={project}
                            dataForQuoteOrDesignForm={partsOptions}
                            setNextStepAfterSave={nextStep => {
                              nextStepAfterSave.current = nextStep;
                            }}
                          />
                        )}
                      </Formik>
                      {createDesignRequest.error && <FormErrorBox error={createDesignRequest.error} />}
                    </>
                  )}
                </GetDataForQuoteOrDesignForm>
              </AssertUserHasPermission>
            </PageContainer>
          </>
        );
      }}
    </GetRequest>
  );
};
